import React, { useEffect, useState, useRef } from 'react';
import kebabCase from 'lodash/kebabCase';
import capitalize from 'lodash/capitalize';
import cn from 'classnames';
import { compose } from 'react-apollo';
import { connect } from 'react-redux';
import Icon from 'chameleon/packages/blue-book-player/library/icon';
import Popover from 'chameleon/packages/blue-book-player/library/popover';
import { BLUEBOOK_TOOLBAR_OPTIONS } from 'chameleon/packages/blue-book-player/constants/preferences';
import { OPTIONS_ID } from 'chameleon/packages/blue-book-player/ToolbarManager/constants';
import { savePreferenceSuccess as savePreferenceSuccessAction } from 'chameleon/packages/blue-book-player/state/reducer';
import Button from 'chameleon/packages/blue-book-player/library/button';
import NotAvailableInBlueBookContent from 'chameleon/packages/blue-book-player/ToolbarManager/NotAvailableInBlueBookContent';
import { HYBRID_COURSES_SUBJECTS } from 'chameleon/packages/blue-book-player/constants/subjects';
import { isImpersonating } from 'chameleon/packages/blue-book-player/utils';
import ButtonWithIcon from '../library/button-with-icon';

const OPTIONS_NOT_AVAILABLE_IN_BLUEBOOK = [
  OPTIONS_ID.IMAGE_UPLOAD,
  OPTIONS_ID.SPECIAL_CHARACTERS,
];

const Option = ({
  option,
  element,
  editorTouched,
  isBluebookDigitalExam,
  savePreference,
  userData,
  preferenceValue = {},
  meQuery,
  savePreferenceSuccess,
  subjectId,
  isSecure,
}) => {
  const [isActive, setIsActive] = useState(false);
  const [isWarningChecked, setIsWarningChecked] = useState(false);

  const popoverRef = useRef();
  let Component;
  const classSelector =
    typeof option === 'string'
      ? `.lrn-editor-toolbar-button-${option}`
      : option.classSelector;
  const customHandler = option.customHandler || (() => {});
  const isDisabled = option.calculateIsDisabled?.(editorTouched) || false;

  const { id: optionId } = option;

  const handleLearnosityClick = () => {
    element.querySelector(classSelector).click();
  };

  const isPreferenceWarningChecked = !!preferenceValue[optionId];
  const shouldRenderWarningPopover =
    OPTIONS_NOT_AVAILABLE_IN_BLUEBOOK.includes(optionId) &&
    isBluebookDigitalExam &&
    !isPreferenceWarningChecked &&
    !HYBRID_COURSES_SUBJECTS.includes(parseInt(subjectId, 10));

  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (classSelector) {
      handleLearnosityClick(e);
    }

    if (customHandler) {
      customHandler(e, element, isSecure);
    }
  };

  const handleClosePopover = () => {
    popoverRef?.current?.close();
  };

  const onWarningAgree = () => {
    handleClosePopover();
    if (isWarningChecked && !isImpersonating()) {
      savePreference({
        variables: {
          userId: userData.me.initId,
          preference: BLUEBOOK_TOOLBAR_OPTIONS,
          value: JSON.stringify({
            ...preferenceValue,
            [optionId]: isWarningChecked,
          }),
        },
      }).then(() => {
        meQuery.refetch().then(({ data }) => {
          savePreferenceSuccess(data.me);
        });
      });
    }

    if (classSelector) {
      handleLearnosityClick();
    }

    if (customHandler) {
      customHandler();
    }
  };

  const shouldShowTooltipBluebookInfo =
    OPTIONS_NOT_AVAILABLE_IN_BLUEBOOK.includes(optionId) &&
    isBluebookDigitalExam;

  const tooltipWarningInfo = shouldShowTooltipBluebookInfo
    ? '; AP Classroom only'
    : '';

  if (!option.icon) {
    const tooltip = option.tooltip || option;
    Component = (onOptionClick) => (
      <Button
        isIcon
        isBlueBookIcon
        onClick={onOptionClick}
        onMouseDown={(e) => e.preventDefault()}
        disabled={isDisabled}
        className="p-2 focus:outline-none"
        title={capitalize(tooltip) + tooltipWarningInfo}
      >
        {option.textIcon ? (
          <span>{option.textIcon}</span>
        ) : (
          <span
            aria-hidden="true"
            className={`lrn_icon lrn-icon-${kebabCase(option)}`}
          />
        )}
      </Button>
    );
  } else {
    const { icon, tooltip, hasDropdown } = option;

    Component = (onOptionClick) => (
      <ButtonWithIcon
        disabled={isDisabled}
        size="custom"
        isBlueBookIcon
        isIcon
        icon={icon}
        onClick={onOptionClick}
        iconClassName={cn({ 'fill-white': isActive })}
        className="p-2 focus:outline-none"
        iconWidth="20"
        iconHeight="20"
        onMouseDown={(e) => e.preventDefault()}
        title={tooltip + tooltipWarningInfo}
        children={
          hasDropdown ? (
            <Icon
              className={cn({ 'fill-white': isActive })}
              id="dropdown_arrow"
            />
          ) : null
        }
        iconOrientation="left"
        containerClassName="!pl-0 flex"
      />
    );
  }

  useEffect(() => {
    const optionElement = element.querySelector(classSelector);
    if (!optionElement) return;

    const observer = new MutationObserver((event) => {
      const learnosityButtonClasses = event[0].target.className;

      if (learnosityButtonClasses.includes('active')) {
        setIsActive(true);
      } else {
        setIsActive(false);
      }
    });
    observer.observe(optionElement, {
      attributes: true,
      attributeFilter: ['class'],
      childList: false,
      characterData: false,
    });

    return () => {
      observer.disconnect(optionElement);
    };
  }, [element]);

  const onCheckboxClick = (isChecked) => {
    setIsWarningChecked(isChecked);
  };

  const renderOptionButton = (onOptionClick = () => {}) => (
    <div
      className={cn(
        'flex items-center justify-center rounded h-[30px] min-w-[30px] my-[3px] mx-1 first:ml-0 border border-[transparent]',
        {
          'bg-bluebook-gray6  hover:bg-bluebook-gray7  hover:border-bluebook-gray6':
            isActive,
          'hover:bg-bluebook-gray2': !isActive && !isDisabled,
        },
      )}
    >
      {Component(onOptionClick)}
    </div>
  );

  const renderPopoverEnabledOption = () => (
    <Popover
      hideArrow
      position="top-center"
      hasCustomStyles
      trigger={renderOptionButton(() => {})}
      ref={popoverRef}
      size="medium"
    >
      <NotAvailableInBlueBookContent
        onCheckboxClick={onCheckboxClick}
        onWarningAgree={onWarningAgree}
      />
    </Popover>
  );

  return shouldRenderWarningPopover
    ? renderPopoverEnabledOption()
    : renderOptionButton(onClick);
};

export default compose(
  connect(({ userData }) => ({ userData }), {
    savePreferenceSuccess: savePreferenceSuccessAction,
  }),
)(Option);
